import { useState, useEffect } from "react";

export interface UseAnnouncementCarouselOptions {
  totalAnnouncements: number;
  rotateIntervalMs?: number;
  fadeDurationMs?: number;
  fadeInDelayMs?: number;
}

export interface UseAnnouncementCarouselReturn {
  currentIndex: number;
  isFading: boolean;
  isVisible: boolean;
}

export const useAnnouncementCarousel = ({
  totalAnnouncements,
  rotateIntervalMs = 5000,
  fadeDurationMs = 700,
  fadeInDelayMs = 50,
}: UseAnnouncementCarouselOptions): UseAnnouncementCarouselReturn => {
  const [ currentIndex, setCurrentIndex ] = useState(0);
  const [ isFading, setIsFading ] = useState(false);

  useEffect(() => {
    if (currentIndex > totalAnnouncements - 1) {
      setCurrentIndex(0);
    }
  }, [ totalAnnouncements, currentIndex ]);

  useEffect(() => {
    if (totalAnnouncements <= 1) {
      setIsFading(false);
      return;
    }

    let cancelled = false;
    const timers: Array<ReturnType<typeof setTimeout>> = [];

    const wait = (ms: number) => new Promise<void>((resolve) => {
      const id = setTimeout(resolve, ms);
      timers.push(id);
    });

    const run = async () => {
      while (!cancelled) {
        await wait(rotateIntervalMs);
        if (cancelled) {
          return;
        }

        setIsFading(true);
        await wait(fadeDurationMs);
        if (cancelled) {
          return;
        }

        setCurrentIndex((prev) => (prev + 1) % totalAnnouncements);
        await wait(fadeInDelayMs);
        if (cancelled) {
          return;
        }

        setIsFading(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
      timers.forEach((id) => clearTimeout(id));
    };
  }, [ totalAnnouncements, rotateIntervalMs, fadeDurationMs, fadeInDelayMs ]);

  return {
    currentIndex,
    isFading,
    isVisible: !isFading,
  };
};
